<template>
    <Dialog v-model:visible="internalVisible" modal header=" " :style="{ width: '50vw' }"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }" @hide="closeModal()"
    >
        <div class="flex flex-wrap">
            <div id="course-info" class="w-full md:w-1/2 bg-light-white border border-light-white rounded md:px-2">
                <CourseDescriptionComponent 
                    :course-data="course"
                    :type="'simple'"
                    :custom-css="'flex flex-wrap md:px-5 gap-5 tracking-wide justify-between'"
                />
            </div>
            
            <div class="w-full md:w-1/2 px-5 bg-iron text-chain rounded-sm shadow-lg pt-3 pb-5">
                <p class="font-bold text-xl border-b border-chain pb-2">{{ translate('order summary', 'ucfirst') }}</p>

                <div class="flex justify-between pb-5 mt-3">
                    <p class="font-semibold">
                        {{ translate('total', 'ucfirst') }} (1 {{ translate('course') }}):
                    </p>
                    <p class="font-bold text-xl">
                        {{ getTotalAmount() }}
                    </p>
                </div>

                <p class="font-semibold text-sm mt-8">
                    {{ translate('By completing your purchase, you agree to these terms and conditions.') }} 
                    <span class="underline cursor-pointer" @click="urlHandler.redirectBlank('/terms-policy')">
                        {{ translate('terms of use', 'capitalize') }}
                    </span>.
                </p>

                <p class="font-semibold text-sm mt-1">
                    {{ translate('You will receive a new period of 12 months.') }}
                </p>
            </div>
        </div>

        <Divider />

        <div class="grid grid-cols-1 text-center">
            <template v-if="!isButtonDisabled">
                <Button 
                    :label="translate('generate payment link', 'ucfirst')" 
                    icon="pi pi-link" 
                    class="bg-button mt-10 w-full md:w-2/3 md:mx-auto"
                    :disabled="isButtonDisabled" 
                    @click.prevent="generatePaymentLinkAndRedirect()" 
                />
                <small class="mt-3 mb-3" v-if="!showSupportButton">
                    {{ translate('By clicking the button above, a payment link will be generated and you will be redirected.') }}
                </small>
            </template>
            <template v-else>
                <Button :label="translate('view pending payments', 'ucfirst')" icon="pi pi-receipt" class="no-bg-button mt-10 w-full md:w-2/3 md:mx-auto"
                    @click.prevent="goToPaymentsView()" />

                <template v-if="paymentLinkButton">
                    <Button :label="translate('access payment link', 'ucfirst')" icon="pi pi-dollar" class="no-bg-button mt-2 w-full md:w-2/3 md:mx-auto"
                        @click.prevent="urlHandler.openExternal(paymentLinkButton)" />
                    <small class="mt-3">
                        {{ translate('You already have a payment link for this/these course(s).') }}
                    </small>
                </template>
                <template v-else>
                    <Button :label="translate('contact support', 'ucfirst')" icon="pi pi-phone" class="bg-purple-suit-forced mt-2 w-full md:w-2/3 md:mx-auto"
                        @click.prevent="urlHandler.redirectAndScroll('/dashboard/support')" />
                </template>
            </template>

            <Button v-if="showSupportButton" :label="translate('contact support', 'ucfirst')" icon="pi pi-phone" class="bg-purple-suit-forced mt-2 w-full md:w-2/3 md:mx-auto"
                @click.prevent="urlHandler.redirectAndScroll('/dashboard/support')" />
        </div>
    </Dialog>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, onMounted, onUnmounted } from 'vue';
import Dialog from 'primevue/dialog';
import { translate } from '@/composables/translate';
import PaymentService from '@/services/PaymentService';
import { showToast } from '@/composables/toast';
import { Divider, useToast, Button } from 'primevue';
import CourseDescriptionComponent from '../Course/CourseDescriptionComponent.vue';
import urlHandler from '@/composables/urlHandler.js';
import { useAuthStore } from '@/stores/auth.js';
import VariableHandler from '@/composables/handlers/VariableHandler.js';
import { sleep } from '@/composables/sleep.js';
import CourseService from '@/services/CourseService.js';

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    },
    course: {
        type: Object,
        default: null
    }
});

const authStore = useAuthStore();
const toast = useToast();
const emit = defineEmits(['update:visible']);
const internalVisible = ref(props.visible);

const course = ref(null);
const loading = ref(false);
const showSupportButton = ref(false);
const paymentUrl = ref('');
const payment = ref(null);
const currency = 'R$';
const paymentLinkButton = ref(null);
const isLoopActive = ref(false);
const isButtonDisabled = ref(false);
const handleActionsCalled = ref(false);

onMounted(() => {
    internalVisible.value = props.visible;
    course.value = props.course;

    if (!course.value) {
        emit('update:visible', false);
        return;
    }

    if (!authStore.isAuthenticated) {
        showToast(toast, translate('Log in to your account to continue'), 'error');
        emit('update:visible', false);
        return;
    }

    handleActions();
});

onUnmounted(() => {
    isLoopActive.value = false;
});

watch(() => props.course, (newVal) => {
    course.value = newVal;
    handleActions();
});

watch(() => props.visible, (newVal) => {
    internalVisible.value = newVal;
    handleActions();
});

watch(internalVisible, (newVal) => {
    if (newVal !== props.visible) {
        emit('update:visible', newVal);
    }
});

function closeModal() {
    isLoopActive.value = false;
    handleActionsCalled.value = false;
    emit('update:visible', false);
}

async function handleActions() {
    if (handleActionsCalled.value) {
        return;
    }

    await canBuyCourses();
    checkIfPaymentWasCompleted();
    handleActionsCalled.value = true;
}

async function generatePaymentLinkAndRedirect() {
    const uuids = [course.value.uuid];

    loading.value = true;
    const response = await PaymentService.generatePaymentLink(uuids);
    loading.value = false;

    if (!response.success()) {
        const responseDataType = response.getContent().type || null;
        if (responseDataType && responseDataType == 'ALREADY_HAVE_COURSE') {
            showToast(toast, translate('you already have bought some of these courses, please check'), 'error');
            return;
        }

        showToast(toast, response.getContent().message, 'error');
        showSupportButton.value = true;
        return;
    }

    payment.value = response.getContent();
    const generatedPaymentUrl = payment.value.direction;

    paymentUrl.value = generatedPaymentUrl;
    PaymentService.openPaymentLink(generatedPaymentUrl);
    await canBuyCourses();
}

function getTotalAmount() {
    let totalAmount = parseFloat(course.value.price);
    return `${currency} ${totalAmount.toFixed(2)}`;
}

async function canBuyCourses() {
    if (!course.value) {
        return;
    }

    const itemsIds = [course.value.id];
    const response = await PaymentService.getPendingPayments();

    let areEqual = false;
    for (let payment of response) {
        const courseIds = payment.course_ids;

        areEqual = VariableHandler.areArraysEqual(courseIds, itemsIds);
        if (areEqual) {
            paymentLinkButton.value = payment.payment_link
            break;
        }
    }

    isButtonDisabled.value = areEqual;
    return areEqual;
}

/**
 * Loops for 30 minutes awaiting for payment aproval
 */
async function checkIfPaymentWasCompleted() {
    if (isLoopActive.value) {
        console.warn('Payment check loop is already running. Ignoring duplicate call.');
        return; 
    }

    isLoopActive.value = true;

    // 60 tentativas com intervalos de 5 segundos = 120 segundos (5 minutos) de janela de espera.
    // Muito mais seguro para o comportamento real de webhooks.
    const maxAttempts = 60; 
    const delayBetweenAttempts = 5000; 

    for (let i = 0; i < maxAttempts; i++) {
        if (!isLoopActive.value) {
            break; 
        }

        if (i == 0) {
            await sleep(1000);

        } else {
            await sleep(delayBetweenAttempts);
        }
        
        let uuid = course.value.uuid;
        
        const redirectUrl = uuid ? `/dashboard/course/${uuid}` : `/dashboard/`;
        if (!uuid) {
            continue;
        }

        try {
            if (!isLoopActive.value) break;
            console.log('chamando')

            const isPayed = await CourseService.isMyCourse(uuid);
            if (isPayed) {
                isLoopActive.value = false;
                showToast(toast, translate('payment received', 'ucfirst'));
                urlHandler.redirectAndScroll(redirectUrl);
                break;
            }
        } catch (error) {
            console.error("Error verifying payment status:", error);
        }
    }
}

function goToPaymentsView() {
    urlHandler.redirectAndScroll('/dashboard/payment_history', { status: 'pending' });
    return;
}

</script>